import axios, { AxiosError, AxiosResponse } from "axios";

import { env } from "@/env.js";
import { Environment } from "@/providers/AuthProvider";

export const ID_PLACEHOLDER = ":id";
export const API_VERSION_HEADER = "X-Prequel-Api-Version";
export const DEFAULT_API_VERSION = "2023-12-01";

let currentEnvironment: Environment = Environment.PROD;

export const setAxiosEnvironment = (environment: Environment) => {
  currentEnvironment = environment;
};

export const getAxiosEnvironment = () => currentEnvironment;

const instance = axios.create({
  baseURL: env.VITE_APP_API_SERVER,
  withCredentials: true,
});

instance.defaults.headers.common["Content-Type"] = "application/json";
instance.defaults.headers.common[API_VERSION_HEADER] = DEFAULT_API_VERSION;

export let handle401: (() => void) | null = null;

export const set401Handler = (handler: () => void) => {
  handle401 = handler;
};

instance.interceptors.request.use((config) => {
  if (config.headers) {
    config.headers["Environment"] = currentEnvironment;
  }

  if (config.data?.destination && typeof config.data === "object" && !Array.isArray(config.data)) {
    delete config.data.destination.type; // Delete the type field from destination
  }

  if (config.data?.source && typeof config.data === "object" && !Array.isArray(config.data)) {
    delete config.data.source.type; // Delete the type field from source
  }
  return config;
});

// Response interceptor for 401 - registered at module load time
instance.interceptors.response.use(
  (response) => response,
  (error) => {
    // Skip for auth-status to avoid loop: 401 -> handle401 -> refetch auth-status -> 401 -> ...
    const isAuthStatusRequest = error.config?.url?.split("?")[0] === ApiRoutes.AUTH_STATUS; // strip query params from the url
    if (error.response?.status === 401 && handle401 && !isAuthStatusRequest) {
      handle401();
    }
    return Promise.reject(error);
  },
);

export enum ApiRoutes {
  AUTH_ENVIRONMENT = "/auth/environment",
  AUTH_STATUS = "/auth-status",
  AUTH_IAM_TRUST_POLICY = "/auth/iam-trust-policy",
  CANCEL_EXPORT_TRANSFER = "/export/transfers/:id/cancel",
  DESTINATIONS = "/destinations",
  DESTINATION_ACTIVITY = "/activity",
  DESTINATION_MODELS = "/destinations/:id/enabled-models",
  GET_DESTINATION_DOCS = "/docs",
  LOGS = "/logs",
  GENERATE_PUBLIC_KEY = "/actions/generate-ssh-key",
  TRANSFERS = "/transfers",
  LOG_OUT = "/logout",
  MAGIC_LINKS = "/magic-links",
  MODELS = "/models",
  VALIDATE_EXPORT_MODEL = "/export/models/:id/validate",
  ORGS = "/orgs",
  EXPORT_PRODUCTS = "/export/products",
  EGRESS_IPS = "/egress-ips",
  PUBLIC_GOOGLE_OAUTH_LOGIN = "/public/auth/google-oauth-login",
  PUBLIC_PASSWORDLESS_LOGIN = "/public/auth/passwordless-login",
  PUBLIC_VALIDATE_PASSWORDLESS_CODE = "/public/auth/validate-passwordless-code",
  PUBLIC_RETRIEVE_TOKEN = "/public/auth/retrieve-token",
  PUBLIC_SOURCE_FORM = "/public/forms/source-vendors",
  PUBLIC_SOURCE_VENDORS = "/public/source-vendors",
  PUBLIC_SSO_ENABLED = "/public/auth/sso-enabled",
  PUBLIC_SSO_LOGIN = "/public/auth/sso-login",
  PUBLIC_MAGIC_LINK_FORM = "/public/forms/magic-link-vendors",
  RECIPIENTS = "/recipients",
  RECIPIENT_SOURCE_CREDENTIALS = "/recipient-source-credentials",
  REPORT_TRANSFERS = "/reports/transfers",
  SOURCES = "/sources",
  TEST_DESTINATION = "/test-destination",
  TEST_EXISTING_DESTINATION = "/destinations/:id/test-connection",
  TEST_SOURCE = "/test-source",
  TEST_EXISTING_SOURCE = "/sources/:id/test-connection",
  TEST_RECIPIENT_SOURCE_CREDENTIALS = "/test-recipient-source-credential",
  TRANSFER = "/transfer",
  USER = "/me",
  USERS = "/users",
  WEBHOOKS = "/webhooks",
}

// ApiResponse and ErrorResponse are what we receive in then/catch clauses
export type ApiResponse<T> = AxiosResponse<{
  status: string;
  data: T;
  message: string;
  has_next?: boolean;
  next_url?: string;
}>;

export type ErrorResponse = AxiosError<{ message: string }, any>;

// AppError is the error type we pass around the application
export type AppError = {
  error: {
    message: string;
    suppressGlobalNotification?: boolean;
    statusCode?: number;
  };
};

export type RequestStatus = "processing" | "success" | "error" | undefined;

export type TestConnectionResponse = {
  status: RequestStatus;
};

export type TestConnectionResult = TestConnectionResponse & {
  message?: string;
};
export type ResourceRequest = TestConnectionResult;

export default instance;
